// Blob contact shadows (§12.3): always on, on every tier, in one instanced draw call. Each body gets
// a radial quad on the ground below it (radius r·(1.1 + 0.25·h), opacity 0.45·clamp(1 − h/4, 0.2, 1),
// h = height above ground). Over void the shadow moves to the void plane and shrinks as the body falls.
import {
  Color, DynamicDrawUsage, InstancedMesh, Matrix4, PlaneGeometry, ShaderMaterial, type Texture,
} from 'three';
import { TUNE, VOID } from '@shared/constants';
import { EK } from '@shared/ids';
import type { FrameView } from '../../game/FrameView';
import { INK } from '../materials/library';
import type { MapProbe } from '../maps/probe';

const MAX_SHADOWS = 96;
const LIFT = 0.015;
const SHADOW = { base: 1.1, perHeight: 0.25, opacity: 0.45, fadeHeight: 4, minFade: 0.2 } as const;
/** Entity kinds that cast a blob shadow, with their footprint radius. */
const ENTITY_RADIUS: Partial<Record<EK, number>> = {
  [EK.CRATE]: TUNE.crate.radius, [EK.BOMB]: 0.35, [EK.CUBE]: TUNE.cube.radius, [EK.CROWN]: 0.4,
  [EK.DUMMY]: TUNE.dummy.radius, [EK.PEBBLE]: 0.25, [EK.PLUNGER]: 0.22,
};

const VERTEX = /* glsl */ `
varying vec2 vUv;
varying float vOpacity;
void main() {
  vUv = uv;
  vOpacity = instanceColor.r;
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}`;
const FRAGMENT = /* glsl */ `
uniform sampler2D uMap;
uniform vec3 uColor;
varying vec2 vUv;
varying float vOpacity;
void main() {
  gl_FragColor = vec4(uColor, texture2D(uMap, vUv).a * vOpacity);
  #include <colorspace_fragment>
}`;

export interface BlobShadows {
  readonly mesh: InstancedMesh;
  /** `slopeX/slopeY`: the floor gradient of a tilted map surface (0 = flat). */
  update(view: FrameView, probe: MapProbe | null, slopeX?: number, slopeY?: number): void;
  dispose(): void;
}

export function createBlobShadows(texture: Texture): BlobShadows {
  const geo = new PlaneGeometry(1, 1);
  geo.rotateX(-Math.PI / 2);
  const mat = new ShaderMaterial({
    uniforms: { uMap: { value: texture }, uColor: { value: new Color(INK) } },
    vertexShader: VERTEX, fragmentShader: FRAGMENT,
    transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  const mesh = new InstancedMesh(geo, mat, MAX_SHADOWS);
  mesh.name = 'blobShadows';
  mesh.frustumCulled = false;
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  const m = new Matrix4();
  const opacity = new Color();
  for (let i = 0; i < MAX_SHADOWS; i++) mesh.setColorAt(i, opacity);
  mesh.instanceColor?.setUsage(DynamicDrawUsage);
  let count = 0;

  /** Floor gradient of a tilted map surface (the Teacup saucer), height per metre along sim x / y. */
  let gx = 0;
  let gy = 0;

  function add(probe: MapProbe | null, x: number, y: number, z: number, r: number): void {
    if (count >= MAX_SHADOWS) return;
    const g = probe ? probe.groundAt(x, y) : 0;
    const overVoid = g <= VOID / 2;
    const sx = overVoid ? 0 : gx;
    const sy = overVoid ? 0 : gy;
    const floor = overVoid ? TUNE.edge.killZ : g + sx * x + sy * y;
    const h = Math.max(0, z - floor);
    const radius = r * (SHADOW.base + SHADOW.perHeight * h);
    const alpha = SHADOW.opacity * Math.min(1, Math.max(SHADOW.minFade, 1 - h / SHADOW.fadeHeight));
    // A flat quad sheared onto the floor plane: local (u, 0, v) → (u, sx·u + sy·v, v).
    const d = radius * 2;
    m.set(
      d, 0, 0, x,
      sx * d, 1, sy * d, floor + LIFT,
      0, 0, d, y,
      0, 0, 0, 1,
    );
    mesh.setMatrixAt(count, m);
    mesh.setColorAt(count, opacity.setRGB(alpha, 0, 0));
    count++;
  }

  return {
    mesh,
    update(view, probe, slopeX = 0, slopeY = 0) {
      count = 0;
      gx = slopeX;
      gy = slopeY;
      for (let i = 0; i < view.beans.length; i++) {
        const b = view.beans[i];
        if (b.active && !b.out && !b.ghost && b.z > TUNE.edge.killZ) add(probe, b.x, b.y, b.z, b.radius);
      }
      for (let i = 0; i < view.ents.length; i++) {
        const e = view.ents[i];
        const r = ENTITY_RADIUS[e.kind];
        // Hidden props (respawning cubes and dummies) cast nothing.
        if (r === undefined || ((e.kind === EK.CUBE || e.kind === EK.DUMMY) && e.timer > 0)) continue;
        // A held crown rides its holder's head; the holder's own shadow covers it.
        if (e.kind === EK.CROWN && e.data < view.beans.length && view.beans[e.data].holdsCrown) continue;
        add(probe, e.x, e.y, e.z, r);
      }
      mesh.count = count;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    dispose() {
      geo.dispose();
      mat.dispose();
      mesh.dispose();
    },
  };
}
